
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { Send } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

type SmsNotification = {
  id: string;
  recipient_phone: string;
  message: string;
  status: string;
  created_at: string;
  sent_at: string | null;
  error_message: string | null;
  gateway_provider: string;
  gateway_message_id: string | null;
  gateway_status: string | null;
  gateway_response: any | null;
};

export const SmsManager = () => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [recipientPhone, setRecipientPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const { data: smsNotifications, refetch } = useQuery({
    queryKey: ["smsNotifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sms_notifications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as SmsNotification[];
    },
  });

  const handleSendSms = async () => {
    try {
      // Først indsæt i databasen
      const { error: insertError } = await supabase.from("sms_notifications").insert({
        recipient_phone: recipientPhone,
        message: message,
        status: "pending",
        gateway_provider: "gatewayapi",
      });

      if (insertError) throw insertError;

      // Kald derefter edge function for at sende SMS'en
      setIsSending(true);
      const { error } = await supabase.functions.invoke('send-sms');
      
      if (error) {
        console.error('Fejl ved afsendelse af SMS:', error);
        toast({
          title: "Fejl",
          description: "Der skete en fejl ved afsendelse af SMS. Prøv igen senere.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "SMS planlagt",
          description: "SMS'en vil blive sendt snarest.",
        });
        setIsOpen(false);
        setRecipientPhone("");
        setMessage("");
      }
      
      refetch();
    } catch (error) {
      console.error("Fejl ved afsendelse af SMS:", error);
      toast({
        title: "Fejl",
        description: "Der skete en fejl ved afsendelse af SMS. Prøv igen senere.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "sent":
        return "bg-green-100 text-green-800";
      case "error":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">SMS Administration</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button>
              <Send className="w-4 h-4 mr-2" />
              Send ny SMS
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send ny SMS</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="phone">Telefonnummer</label>
                <Input
                  id="phone"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="+45 12345678"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="message">Besked</label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Skriv din besked her..."
                />
              </div>
              <Button 
                onClick={handleSendSms} 
                className="w-full"
                disabled={isSending}
              >
                {isSending ? 'Sender...' : 'Send SMS'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-4">Modtager</th>
              <th className="text-left p-4">Besked</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Gateway Status</th>
              <th className="text-left p-4">Oprettet</th>
              <th className="text-left p-4">Sendt</th>
            </tr>
          </thead>
          <tbody>
            {smsNotifications?.map((sms) => (
              <tr key={sms.id} className="border-b">
                <td className="p-4">{sms.recipient_phone}</td>
                <td className="p-4">{sms.message}</td>
                <td className="p-4">
                  <span
                    className={`px-2 py-1 rounded-full text-sm ${getStatusBadgeColor(
                      sms.status
                    )}`}
                  >
                    {sms.status}
                    {sms.error_message && (
                      <span className="block text-xs text-red-600">
                        {sms.error_message}
                      </span>
                    )}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-sm">
                    {sms.gateway_status || "-"}
                  </span>
                </td>
                <td className="p-4">
                  {format(new Date(sms.created_at), "d. MMM yyyy HH:mm", {
                    locale: da,
                  })}
                </td>
                <td className="p-4">
                  {sms.sent_at
                    ? format(new Date(sms.sent_at), "d. MMM yyyy HH:mm", {
                        locale: da,
                      })
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
